// Étapes d'une réponse de Nexus (recherches, lectures, notes), construites à partir des événements « activity » du
// flux. Le début et la fin d'un même outil portent le même identifiant d'étape (backend : nexus/steps.py) ; l'ancien
// moteur n'en donne pas, on rapproche alors la fin de la dernière étape en cours du même outil.

const findLastIndex = (items, predicate) => {
  for (let index = items.length - 1; index >= 0; index -= 1) {
    if (predicate(items[index])) return index;
  }
  return -1;
};

export const startRun = (now = Date.now()) => ({ steps: [], startedAt: now });

const outcome = (data) => (data.status === 'error' ? 'error' : 'success');

// Commentaire de Nexus entre deux outils (« Je cherche d'abord le projet. ») : une note dans les étapes.
export const applyNote = (run, text, now = Date.now()) => {
  const note = (text || '').trim();
  if (!run || !note) return run;
  return { ...run, steps: [...run.steps, { id: `note-${run.steps.length}-${now}`, kind: 'note', label: note, status: 'done' }] };
};

export const applyActivity = (run, data, now = Date.now()) => {
  if (!run || !data) return run;
  const phase = data.phase || 'activity';
  const { steps } = run;
  if (phase === 'tool_call' || phase === 'tool_result') {
    let index = data.step ? steps.findIndex((step) => step.id === data.step) : -1;
    if (index < 0 && phase === 'tool_result' && !data.step) {
      index = findLastIndex(steps, (step) => step.status === 'running' && step.tool === data.tool);
    }
    if (phase === 'tool_call' || index < 0) {
      const step = {
        id: data.step || `step-${steps.length}-${now}`,
        tool: data.tool || '',
        label: data.label || data.tool_label || data.activity || data.tool || 'Outil',
        detail: data.detail || '',
        kind: data.kind || 'other',
        internal: Boolean(data.internal),
        status: phase === 'tool_call' ? 'running' : outcome(data),
        startedAt: now,
        duration: data.duration ?? null,
        error: data.error || '',
        scope: data.scope || '',
      };
      return { ...run, steps: [...steps, step] };
    }
    const previous = steps[index];
    const updated = {
      ...previous,
      status: outcome(data),
      duration: data.duration ?? Math.max(0, (now - previous.startedAt) / 1000),
      error: data.error || '',
      scope: data.scope || previous.scope || '', // projet ou document réellement regardé (fin de l'outil)
    };
    return { ...run, steps: steps.map((step, i) => (i === index ? updated : step)) };
  }
  if (phase === 'routing' && data.activity) {
    return applyNote(run, data.activity, now);
  }
  return run; // battements de cœur, confirmations : pas des étapes
};

// Fin de la réponse : une étape restée « en cours » (événement de fin perdu) est considérée comme terminée.
export const closeRun = (run, now = Date.now()) => (run ? {
  ...run,
  endedAt: now,
  steps: run.steps.map((step) => (step.status === 'running' ? { ...step, status: 'success' } : step)),
} : run);

export const visibleSteps = (run) => (run?.steps || []).filter((step) => !step.internal);

export const currentStep = (run) => {
  const running = visibleSteps(run).filter((step) => step.status === 'running');
  return running[running.length - 1] || null;
};

export const formatSeconds = (seconds) => {
  const value = Math.max(0, Math.round(seconds || 0));
  if (value < 60) return `${value} s`;
  return `${Math.floor(value / 60)} min ${String(value % 60).padStart(2, '0')}`;
};

export const formatStepDuration = (duration) => {
  if (duration === null || duration === undefined) return '';
  if (duration < 1) return `${Math.max(0.1, Math.round(duration * 10) / 10).toLocaleString('fr-FR')} s`;
  return formatSeconds(duration);
};

export const runSummary = (run, now = Date.now()) => {
  const actions = visibleSteps(run).filter((step) => step.kind !== 'note');
  const errors = actions.filter((step) => step.status === 'error').length;
  const parts = [actions.length ? `${actions.length} étape${actions.length > 1 ? 's' : ''}` : 'Réponse directe'];
  const seconds = ((run?.endedAt ?? now) - (run?.startedAt ?? now)) / 1000;
  if (seconds >= 1) parts.push(formatSeconds(seconds));
  if (errors) parts.push(`${errors} échec${errors > 1 ? 's' : ''}`);
  return parts.join(' · ');
};
